import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { status } from 'constants/general';
import Button from 'modules/common/components/Button';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import categoriesStrings from 'modules/categories/strings';
import {
  fetchRelatedPriceLists,
  resetRelatedPriceLists,
  submitProvidersBulk,
} from 'modules/categories/categoriesSlice';
import { selectedHealthPlanCountrySelector } from 'modules/healthPlans/healthPlansSlice';
import {
  buildRelatedPriceListsQuery,
  filterMatchingPriceListsForSync,
} from 'modules/modals/RelatedPriceListsModal/helper';
import {
  PopupTitleSpan,
  PopupWrapper,
  PopupTitle,
  PopupTitleWrapper,
  Container,
  PopupTitleContainer,
  extendLoaderStyle,
  ButtonsContainer,
  extendGrayButtonStyle,
  extendBlueButtonStyle,
  extendSmallBlueButtonStyle,
  extendAddedButtonStyle,
  ItemsContainer,
  ItemContainer,
  ItemWrapper,
  EmptyState,
  LoadingWrapper,
  Subtitle,
} from 'modules/modals/RelatedPriceListsModal/RelatedPriceListsModal.style';

const SyncDoctorsToPlansModal = ({
  onClose,
  addedProvidersPayload,
  sourcePriceListId,
  sourceHealthPlanId,
  priceValue,
  categoryId,
  beneficiaryCompanyKey,
}) => {
  const dispatch = useDispatch();
  const relatedPriceLists = useSelector(state => state.categories.relatedPriceLists);
  const submitProvidersBulkState = useSelector(state => state.categories.submitProvidersBulk);
  const selectedHealthPlanCountry = useSelector(selectedHealthPlanCountrySelector);
  const [addedPriceListIds, setAddedPriceListIds] = useState([]);
  const [addingPriceListId, setAddingPriceListId] = useState(null);
  const prevSubmitProvidersBulk = useRef(submitProvidersBulkState.status);

  const currencyName = selectedHealthPlanCountry?.currency?.name || '';
  const matchingPriceLists = filterMatchingPriceListsForSync(relatedPriceLists.data, {
    sourcePriceListId,
    sourceHealthPlanId,
  });

  useEffect(() => {
    if (priceValue === undefined || priceValue === null) return;

    dispatch(
      fetchRelatedPriceLists(
        buildRelatedPriceListsQuery({
          categoryId,
          priceValue,
          beneficiaryCompanyKey,
        }),
      ),
    );

    return () => {
      dispatch(resetRelatedPriceLists());
    };
  }, [dispatch, priceValue]);

  useEffect(() => {
    if (
      submitProvidersBulkState.status === status.SUCCESS &&
      prevSubmitProvidersBulk.current !== submitProvidersBulkState.status &&
      addingPriceListId !== null
    ) {
      setAddedPriceListIds(prev => [...prev, addingPriceListId]);
      setAddingPriceListId(null);
    }

    if (
      submitProvidersBulkState.status === status.FAIL &&
      prevSubmitProvidersBulk.current !== submitProvidersBulkState.status
    ) {
      setAddingPriceListId(null);
    }
    prevSubmitProvidersBulk.current = submitProvidersBulkState.status;
  }, [submitProvidersBulkState.status, addingPriceListId]);

  const handleAddToPriceList = priceList => {
    setAddingPriceListId(priceList.id);
    dispatch(
      submitProvidersBulk({
        ...addedProvidersPayload,
        priceListId: priceList.id,
      }),
    );
  };

  const handleClose = () => {
    dispatch(resetRelatedPriceLists());
    onClose();
  };

  const isLoading = relatedPriceLists.status === status.FETCHING;
  const isRowLoading = priceListId =>
    addingPriceListId === priceListId && submitProvidersBulkState.status === status.SUBMITING;

  return (
    <Popup showCloseBtn={false}>
      <PopupWrapper>
        <PopupTitleContainer>
          <PopupTitleWrapper>
            <PopupTitle>{categoriesStrings.syncDoctorsModal.title}</PopupTitle>
            <PopupTitleSpan onClick={handleClose} />
          </PopupTitleWrapper>
        </PopupTitleContainer>

        <Container>
          <Subtitle>{categoriesStrings.syncDoctorsModal.subtitle}</Subtitle>

          {isLoading && (
            <LoadingWrapper>
              <Loader extendStyle={extendLoaderStyle} />
            </LoadingWrapper>
          )}

          {!isLoading && matchingPriceLists.length === 0 && (
            <EmptyState>{categoriesStrings.syncDoctorsModal.emptyState}</EmptyState>
          )}

          {!isLoading && matchingPriceLists.length > 0 && (
            <ItemsContainer>
              <ItemContainer>
                <ItemWrapper>{categoriesStrings.syncDoctorsModal.healthPlan}</ItemWrapper>
                <ItemWrapper>{categoriesStrings.syncDoctorsModal.priceList}</ItemWrapper>
                <ItemWrapper>{categoriesStrings.syncDoctorsModal.consultationFee}</ItemWrapper>
                <ItemWrapper>{categoriesStrings.syncDoctorsModal.action}</ItemWrapper>
              </ItemContainer>
              {matchingPriceLists.map(priceList => {
                const fee = priceList.priceListConfigurations?.[0]?.priceValue;
                const isAdded = addedPriceListIds.includes(priceList.id);

                return (
                  <ItemContainer key={priceList.id}>
                    <ItemWrapper>{priceList.healthPlan?.name}</ItemWrapper>
                    <ItemWrapper>{priceList.name}</ItemWrapper>
                    <ItemWrapper>
                      {fee} {currencyName}
                    </ItemWrapper>
                    <ItemWrapper>
                      <Button
                        btnText={
                          isAdded
                            ? categoriesStrings.syncDoctorsModal.added
                            : categoriesStrings.syncDoctorsModal.addDoctors
                        }
                        onClick={() => handleAddToPriceList(priceList)}
                        extendButtonStyle={
                          isAdded ? extendAddedButtonStyle : extendSmallBlueButtonStyle
                        }
                        disabled={isAdded}
                        isLoading={isRowLoading(priceList.id)}
                        extendLoaderStyle={extendLoaderStyle}
                      />
                    </ItemWrapper>
                  </ItemContainer>
                );
              })}
            </ItemsContainer>
          )}

          <ButtonsContainer>
            <Button
              onClick={handleClose}
              btnText={categoriesStrings.syncDoctorsModal.done}
              extendButtonStyle={extendBlueButtonStyle}
            />
          </ButtonsContainer>
        </Container>
      </PopupWrapper>
    </Popup>
  );
};

SyncDoctorsToPlansModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  addedProvidersPayload: PropTypes.shape({
    providerTypeId: PropTypes.number,
    providers: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  sourcePriceListId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sourceHealthPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  priceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  categoryId: PropTypes.number.isRequired,
  beneficiaryCompanyKey: PropTypes.string,
};

SyncDoctorsToPlansModal.defaultProps = {
  beneficiaryCompanyKey: null,
};

export default SyncDoctorsToPlansModal;
